// Stratus Copilot — floating chat assistant (#220).
//
// A DentChat-style launcher + panel that talks to /api/copilot/chat. The backend
// runs GLM over read-only diagnostic tools, so this widget stays thin: send the
// message + short history, render the grounded answer, show which tools it
// consulted. Mounted once in Layout so it rides every authenticated page.

import { useEffect, useRef, useState } from 'react';
import { X, Send } from 'lucide-react';
import { getToken } from '../lib/api';
import { Orb } from './Orb';

type Msg = { role: 'user' | 'assistant'; content: string; tools?: string[] };

const STORE = 'stratus_copilot_history';
const QUICK = [
  'Why won’t my DID ring the right place?',
  'Show me failed calls today',
  'Are all my trunks registered?',
  'How do I add a SIP trunk?',
];

// Minimal, dependency-free markdown: **bold**, `code`, - bullets, and paragraphs.
function renderMd(text: string) {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s: string) => esc(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-elevated text-[0.85em] font-mono">$1</code>');
  const out: string[] = [];
  let list: string[] = [];
  const flush = () => { if (list.length) { out.push('<ul class="list-disc pl-5 space-y-0.5 my-1">' + list.map(li => `<li>${inline(li)}</li>`).join('') + '</ul>'); list = []; } };
  for (const raw of String(text).split('\n')) {
    const line = raw.replace(/\s+$/, '');
    const m = /^\s*[-*]\s+(.*)$/.exec(line);
    if (m) { list.push(m[1]); continue; }
    flush();
    if (line.trim()) out.push(`<p class="my-1">${inline(line)}</p>`);
  }
  flush();
  return { __html: out.join('') };
}

export default function Copilot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [msgs, setMsgs] = useState<Msg[]>(() => {
    try { return JSON.parse(localStorage.getItem(STORE) || '[]'); } catch { return []; }
  });
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => { try { localStorage.setItem(STORE, JSON.stringify(msgs.slice(-30))); } catch {} }, [msgs]);
  useEffect(() => { if (open) scroller.current?.scrollTo({ top: 1e9, behavior: 'smooth' }); }, [msgs, open, busy]);
  // Live elapsed counter while thinking — proves the widget isn't frozen.
  useEffect(() => {
    if (!busy) { setElapsed(0); return; }
    const started = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 250);
    return () => clearInterval(id);
  }, [busy]);

  async function send(text: string) {
    const q = text.trim();
    if (!q || busy) return;
    const history = msgs.slice(-8).map(m => ({ role: m.role, content: m.content }));
    setMsgs(m => [...m, { role: 'user', content: q }]);
    setInput('');
    setBusy(true);
    // Diagnostics can take a while (the model runs several tool rounds); give it
    // room but never hang forever, and never crash on a non-JSON error page.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 150000);
    try {
      const r = await fetch('/api/copilot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ message: q, history }),
        signal: ctrl.signal,
      });
      const raw = await r.text();
      let j: any = null;
      try { j = raw ? JSON.parse(raw) : null; } catch { /* server returned HTML/plain — handled below */ }
      if (!r.ok || !j) {
        const msg = j?.error
          || (r.status === 502 || r.status === 504 ? 'The assistant took too long to respond — try a more specific question.'
              : `The assistant hit an error (HTTP ${r.status || '?'}). Please try again.`);
        throw new Error(msg);
      }
      setMsgs(m => [...m, { role: 'assistant', content: j.answer || '(no answer)', tools: j.tools_used || [] }]);
    } catch (e: any) {
      const msg = e?.name === 'AbortError'
        ? 'That took too long and timed out. Try asking about a specific number, extension, or trunk.'
        : String(e?.message || e);
      setMsgs(m => [...m, { role: 'assistant', content: `⚠️ ${msg}` }]);
    } finally { clearTimeout(timer); setBusy(false); }
  }

  if (!open) {
    // "Ask Beacon"-style launcher: glass label chip + the living neural orb (#222).
    return (
      <button
        onClick={() => setOpen(true)}
        title="Ask Stratus Copilot"
        aria-label="Ask Stratus Copilot"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2.5 bg-transparent border-0 p-1 cursor-pointer group"
      >
        <span className="text-sm font-semibold px-3 py-1.5 rounded-full bg-elevated/80 border border-border backdrop-blur shadow-lg group-hover:bg-elevated transition">
          Ask Copilot
        </span>
        <Orb size={56} strong />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-40 w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-2rem))] flex flex-col card shadow-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-elevated">
        <Orb size={26} />
        <div className="flex-1">
          <div className="text-sm font-semibold leading-tight">Stratus Copilot</div>
          <div className="text-[11px] text-textSecondary leading-tight">Setup help &amp; live call diagnostics</div>
        </div>
        {msgs.length > 0 && (
          <button className="btn-ghost text-[11px] px-2 py-1" onClick={() => setMsgs([])} title="Clear conversation">Clear</button>
        )}
        <button className="btn-ghost p-1" onClick={() => setOpen(false)} title="Close"><X size={16} /></button>
      </div>

      <div ref={scroller} className="flex-1 overflow-auto px-3 py-3 space-y-3">
        {msgs.length === 0 && (
          <div className="text-sm text-textSecondary space-y-3">
            <p>Hi — I can help you set up Stratus and diagnose live issues. I can read this deployment’s trunks, extensions, routing, and call logs to figure out what’s wrong.</p>
            <div className="space-y-1.5">
              {QUICK.map(q => (
                <button key={q} onClick={() => send(q)}
                  className="block w-full text-left text-sm px-3 py-2 rounded-lg border border-border hover:bg-elevated transition">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-primary text-white' : 'bg-elevated text-textPrimary'}`}>
              {m.role === 'assistant'
                ? <div className="leading-relaxed" dangerouslySetInnerHTML={renderMd(m.content)} />
                : <span className="whitespace-pre-wrap">{m.content}</span>}
              {!!(m.tools && m.tools.length) && (
                <div className="mt-1.5 text-[10px] text-textSecondary border-t border-border/50 pt-1">
                  checked: {m.tools.join(', ')}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-elevated rounded-2xl px-3 py-2 min-w-[180px]">
              <div className="flex items-center gap-2 text-sm text-textSecondary">
                {/* Bouncing dots — always visibly moving so it never reads as frozen. */}
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                <span className="flex-1">{elapsed < 4 ? 'looking into it…' : elapsed < 12 ? 'checking your config…' : 'still working — digging through the logs…'}</span>
                <span className="tabular-nums text-[11px] text-textSecondary/70">{elapsed}s</span>
              </div>
              {/* Indeterminate progress sweep. */}
              <div className="mt-2 h-1 rounded-full bg-border overflow-hidden">
                <div className="h-full w-1/3 rounded-full bg-primary copilot-sweep" />
              </div>
            </div>
          </div>
        )}
      </div>

      <form className="p-3 border-t border-border flex items-end gap-2"
            onSubmit={e => { e.preventDefault(); send(input); }}>
        <textarea
          className="input flex-1 resize-none h-[42px] max-h-32 py-2"
          placeholder="Ask about setup or a call that isn’t working…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
          disabled={busy}
        />
        <button type="submit" className="btn-primary p-2.5 rounded-lg" disabled={busy || !input.trim()}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}
